import React, { FC } from 'react';
import ChipWithTooltip from '@components/ChipWithTooltip';
import IdTitle from '@components/EntityDrawers/components/ReferencesInputsGroup/components/IdTitle';
import useChangeReference from '@components/EntityDrawers/components/ReferencesInputsGroup/hooks/useChangeReference';
import { CommonKeys } from '@components/EntityDrawers/constants/keys';
import useQnousRefOptions from '@components/EntityDrawers/hooks/useQnousRefOptions';
import ListItemWithInfo from '@components/Inputs/Autocomplete/items/ListItemWithInfo';
import ControlledAutocomplete from '@components/Inputs/controllers/ControlledAutocomplete';
import { RefType } from '@constants/entities/project';
import { MAX_VISIBLE_CHIP_SYMBOLS } from '@constants/entities/ui';
import { useProject } from '@context/Project';
import useProjectData from '@hooks/useProjectData';
import useReferenceMaps from '@hooks/useReferenceMaps';

const CisInput: FC = () => {
  const { viewOnly } = useProject();
  const [{ cisOptions }, isRefsLoading] = useQnousRefOptions();
  const [{ cisMap }] = useReferenceMaps();
  const [{ settingsData }] = useProjectData();

  const { handleChangeReference, isChangingReference } = useChangeReference();

  if (!settingsData?.reference_model.includes(RefType.CIS)) return null;

  return (
    <ControlledAutocomplete
      name={CommonKeys.CisRef}
      label="CIS-18 reference"
      placeholder="Select reference"
      options={cisOptions}
      loading={isRefsLoading || isChangingReference}
      multiple
      readOnly={viewOnly}
      itemComponent={ListItemWithInfo}
      itemProps={() => ({
        renderTooltipTitle: ({
          label,
          value,
        }: {
          label: string;
          value: string;
        }) => {
          const normalLabel = label || cisMap.get(value);

          const [id, title] = normalLabel?.split(':') ?? ['', ''];

          return <IdTitle id={id} title={title} color="grey.500" />;
        },
      })}
      onChange={handleChangeReference(CommonKeys.CisRef)}
      isLoading={isChangingReference || isRefsLoading}
      renderTags={(value, getTagProps) => {
        return value.map(({ value, label }, index) => {
          const normalLabel = label || cisMap.get(value);
          const [id, title] = normalLabel?.split(':') ?? ['', ''];

          return (
            <ChipWithTooltip
              key={value}
              label={normalLabel}
              tooltip={{
                title: <IdTitle id={id} title={title} />,
                disableHoverListener:
                  normalLabel?.length < MAX_VISIBLE_CHIP_SYMBOLS,
              }}
              chip={getTagProps({ index })}
            />
          );
        });
      }}
    />
  );
};

export default CisInput;
